import React, { CSSProperties, useMemo, useState } from "react";

import Image, { StaticImageData } from "next/image";

import cx from "classnames";

import { useGetFileURL } from "@/hooks/useUploadFileAvatar";

import { iconDiamond } from "@/utils/avatar";

import { Star } from "@/assets/icons";
import { Spin, Upload, UploadFile } from "antd";
import { EditFilled, LoadingOutlined } from "@ant-design/icons";
import { UploadChangeParam } from "antd/lib/upload";
import { BsCrop } from "react-icons/bs";

export interface IAvatar {
  size?: "xxs" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
  src: string | "default" | StaticImageData | null;
  alt: string;
  tier_position?: "right" | "left";
  type_tier?: "BRONZE" | "SILVER" | "GOLD" | "ORIGINAL" | "DIAMOND" | "PLATINUM";
  badge?: number | string | null;
  isRounded?: boolean;
  staticImg?: boolean;
  badgeXPos?: "center" | "right";
  styleTier?: CSSProperties;
  className?: string;
  quality?: number;
  avatarAuto?: boolean;
  editIcon?: boolean;
  editCrop?: boolean;
  onChangeEdit?: (e: UploadChangeParam<UploadFile<any>>) => void;
  onEditCropClicked?: () => void;
  id?: string;
  key?: string;
  name?: string;
}

const Avatar: React.FC<IAvatar> = ({
  type_tier = "",
  tier_position = "right",
  size = "sm",
  src = null,
  alt = "image avatar",
  badge = null,
  isRounded = true,
  staticImg = false,
  badgeXPos = "right",
  styleTier,
  className,
  quality,
  avatarAuto,
  editIcon,
  onChangeEdit,
  editCrop,
  onEditCropClicked,
  id,
  key,
  name = 'User',
  ...props
}) => {
  const { url: urlImgAvatar } = useGetFileURL((src || name) as string);

  const [isLoadingLoadImg, setLoadingLoadImg] = useState(false);

  const borderAvatar = useMemo(() => {
    const borderMap: Record<string, string> = {
      BRONZE: "border-2 border-[#FF7875]",
      SILVER: "border-2 border-[#8C8C8C]",
      PLATINUM: "border-2 border-[#1890FF]",
      GOLD: "border-2 border-[#FADB14]",
      ORIGINAL: "border-2 border-[#1890FF]",
      DIAMOND: "border-2 border-[#1890FF]",
      default: "",
    };

    return borderMap[type_tier] || borderMap.default;
  }, [type_tier]);

  const sizeWrapper = useMemo(() => {
    const sizeMap: Record<string, string> = {
      xxs: "h-8 w-8",
      xs: "h-12 w-12",
      sm: "h-16 w-16",
      md: "h-20 w-20",
      lg: "h-24 w-24",
      xl: "h-28 w-28",
      "2xl": "h-32 w-32",
      default: "h-10 w-10",
    };

    return sizeMap[size] || sizeMap.default;
  }, [size]);

  const sizeImage = useMemo(() => {
    const sizeMap: Record<
      string,
      {
        width: number;
        height: number;
      }
    > = {
      xxs: { width: 30, height: 30 },
      xs: { width: 48, height: 48 },
      sm: { width: 64, height: 64 },
      md: { width: 80, height: 80 },
      lg: { width: 96, height: 96 },
      xl: { width: 112, height: 112 },
      default: { width: 40, height: 40 },
    };

    return sizeMap[size] || sizeMap.default;
  }, [size]);

  const diamondPosition = useMemo(() => {
    const positionMap: Record<string, string> = {
      xxs: tier_position === "left" ? "-left-2 -bottom-6" : "-right-2 -bottom-3",
      xs: tier_position === "left" ? "-left-2 -bottom-6" : "-right-2 bottom-3",
      sm: tier_position === "left" ? "-left-1 bottom-0" : "-right-2 bottom-4",
      md: tier_position === "left" ? "left-0 bottom-0" : "-right-3 bottom-5",
      lg: tier_position === "left" ? "left-1 bottom-0" : "-right-3 bottom-6",
      xl: tier_position === "left" ? "left-1 bottom-0" : "-right-3 bottom-6",
      "2xl": tier_position === "left" ? "left-1 bottom-0" : "-right-1 bottom-6",
      default: tier_position === "left" ? "left-2 bottom-0" : "-right-2 bottom-0",
    };

    return positionMap[size] || positionMap.default;
  }, [size, tier_position]);

  const badgePosition = useMemo(() => {
    const positionMap: Record<string, string> = {
      xs: isRounded ? "-bottom-[10px] -right-[10px]" : "-bottom-[.75rem] -right-[.75rem]",
      sm: "-bottom-3 -right-3",
      md: "-bottom-[.65rem] -right-[.65rem]",
      lg: "-bottom-[.5rem] -right-[.5rem]",
      xl: "-bottom-[.70rem] -right-[.70rem]",
      "2xl": "bottom-0",
    };

    return positionMap[size] || "bottom-0";
  }, [size, isRounded]);

  const badgeSize = useMemo(() => {
    const sizeMap: Record<string, number> = {
      xs: 32,
      sm: 40,
      md: 40,
      lg: 40,
      xl: 52,
      "2xl": 40,
    };

    return sizeMap[size] || 40;
  }, [size]);

  const fontSize = useMemo(() => {
    const sizeMap: Record<string, string> = {
      xxs: "text-[5px]",
      xs: "text-[5px]",
      md: "text-[6px]",
      xl: "text-[8px]",
      default: "text-[5px]",
    };

    return sizeMap[size] || sizeMap.default;
  }, [size]);

  const srcAvatar = useMemo(() => {
    if (staticImg) {
      return src
    }
    if (urlImgAvatar) {
      return urlImgAvatar;
    }
    return null;
  }, [urlImgAvatar, src, staticImg]);
  // return 'https://picsum.photos/200/300';
  // }, []);

  return (
    <div
      {...props}
      className={cx(
        avatarAuto ? `w-full ${sizeWrapper?.split(" ")?.[0]}` : sizeWrapper,
        borderAvatar,
        { "rounded-full": isRounded },
        { "rounded-lg": !isRounded },
        { "bg-neutral-600": editCrop || editIcon },
        { "bg-neutral-800": !editCrop && !editIcon },
        "inline-flex justify-center relative",
        className,
      )}
    >
      {(isLoadingLoadImg) && (
        <div
          className={cx(
            className,
            avatarAuto ? `w-full ${sizeWrapper?.split(" ")?.[0]}` : sizeWrapper,
            { "rounded-full": isRounded },
            { "rounded-lg": !isRounded },
            "inline-flex justify-center items-center bg-neutral-800 absolute top-0 bottom-0 left-0 right-0 w-full h-full",
          )}
        >
          <div className="inline-block text-center">
            <Spin
              indicator={
                <LoadingOutlined
                  spin
                  width={32}
                  height={32}
                  color="#158284"
                  className="text-primary"
                />
              }
            />
            {/* <ColorRing
              visible={true}
              height="32"
              width="32"
              ariaLabel="color-ring-loading"
              colors={['#158284', '#158284', '#158284', '#158284', '#158284']}
            /> */}
            {["md", "lg", "xl", "2xl"].includes(size) && (
              <p className="text-neutral-300 text-xs mt-2">Loading...</p>
            )}
          </div>
        </div>
      )}

      {srcAvatar && (
        <Image
          key={`avatar-${src}-${srcAvatar}`}
          src={srcAvatar as string}
          alt={alt}
          width={sizeImage.width * 50}
          height={sizeImage.height * 50}
          className={cx(
            "object-cover w-full h-full absolute top-0 left-0",
            { "rounded-full": isRounded },
            { "rounded-lg": !isRounded },
          )}
          quality={quality || 75}
          onLoadingComplete={() => setLoadingLoadImg(false)}
        />
      )}

      {editIcon && (
        <div className="absolute bottom-0 right-0 z-[1] bg-black rounded-full w-6 h-6 border flex justify-center items-center border-white">
          <Upload
            fileList={[]}
            multiple={false}
            showUploadList={false}
            onChange={(e) => onChangeEdit && onChangeEdit(e)}
            accept="image/*"
          >
            <EditFilled className="text-xs" />
          </Upload>
        </div>
      )}

      {editCrop && (
        <div
          role="button"
          onClick={() => onEditCropClicked && onEditCropClicked()}
          className="absolute bottom-0 left-0 z-[1] bg-black rounded-full w-6 h-6 border flex justify-center items-center border-white"
        >
          <BsCrop className="text-xs !text-white" />
        </div>
      )}

      {type_tier && (
        <span
          className={`absolute ${diamondPosition}`}
          style={{ ...styleTier }}
        >
          {iconDiamond(type_tier, size)}
        </span>
      )}

      {badge !== null && (
        <div
          className={cx(
            "flex justify-center items-center absolute -right-1 font-bold",
            badgePosition,
            fontSize,
          )}
        >
          <Star
            size={badgeSize}
            color={
              Number(badge) >= 80
                ? "green"
                : Number(badge) >= 51
                  ? "yellow"
                  : "salmon"
            }
          />
          <p
            className={`absolute text-black translate-y-[2px] ${Number(badge) < 75 && "opacity-0"
              }`}
          >{`${Number(badge).toFixed()}%`}</p>
        </div>
      )}
    </div>
  );
};

export default React.memo(Avatar);
